import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

// Breadcrumb and title on a dark band at the foot of a cover, so they stay readable over any photo or video
export const BannerCaption = ({ label, title, subtitle, as: Title = "p", stack = false }) => (
  <div className={`banner-cap ${stack ? "relative sm:absolute sm:inset-x-0 sm:bottom-0" : "absolute inset-x-0 bottom-0"} pt-12 pb-8 bg-gradient-to-t from-black/90 via-black/65 to-transparent pointer-events-none`}>
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <p className="banner-cap-label font-mono text-[11px] uppercase tracking-[0.15em] text-white/75">
        <Link to="/" className="pointer-events-auto hover:text-white">Home</Link> · {label}
      </p>
      <Title className="banner-cap-title mt-3 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">{title || label}</Title>
      {subtitle && <p className="banner-cap-sub mt-3 max-w-md text-sm leading-relaxed text-white/85">{subtitle}</p>}
    </div>
  </div>
);

// Same heading band as the Method page: breadcrumb and title on a light background above the banner
export const PageHeading = ({ label, title, subtitle, testid = "page-heading" }) => (
  <section data-testid={testid} className="mt-14 pt-14 lg:pt-16 pb-10 lg:pb-12 bg-sand">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-inksoft">
        <Link to="/" className="hover:text-ink">Home</Link> · {label}
      </p>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">{title || label}</h1>
      {subtitle && <p className="mt-3 max-w-md text-sm leading-relaxed text-inksoft">{subtitle}</p>}
    </div>
  </section>
);

// Horizontal cover banner at the top of a page: a video, or photos side by side (each near its own resolution).
// A portrait video (sideVideo) sits on the right half instead of being stretched across the page.
// `aspect` ("1232 / 480"): the banner takes the video's own ratio, so the whole frame shows, never cropped or stretched.
// `ratio` ("16 / 9"): the same at every width; the banner is full width in that shape. A video of the same shape shows whole; a wider one is trimmed evenly at the sides.
// `ratio` + `crop`: the video's own shape on phones, the same height as the other banners from `sm` up, cropped from the top (the bottom of the frame stays); `cropPos` changes which part stays.
export const PageBanner = ({ images = [], video, sideVideo = false, tall = false, mid = false, noCaption = false, rgb = false, poster, rate = 1, aspect, ratio, crop = false, cropPos = "object-bottom", label, title, subtitle, as, testid = "page-banner" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    v.playbackRate = rate;
    v.play().catch(() => {});
  }, [rate]);
  if (aspect) {
    return (
      <section data-testid={testid} className="day-light-bg group relative mt-14 overflow-hidden bg-black">
        <video ref={ref} src={video} poster={poster || (video && video.replace(/\.mp4$/, "-poster.jpg"))} preload="auto" autoPlay muted loop playsInline onLoadedMetadata={(e) => { e.currentTarget.playbackRate = rate; }}
          className="bw-hover block w-full h-auto" style={{ aspectRatio: aspect }} />
        <BannerCaption label={label} title={title} subtitle={subtitle} as={as} stack />
      </section>
    );
  }
  const height = ratio
    ? (crop ? "banner-ratio sm:h-[51vh] sm:min-h-[345px]" : "banner-full")
    : tall ? "h-[59vh] min-h-[380px]" : mid ? "h-[51vh] min-h-[345px]" : "h-[38vh] min-h-[265px]";
  return (
    <section data-testid={testid} style={ratio ? { "--banner-ratio": ratio } : undefined} className={`day-light-bg group relative ${noCaption ? "" : "mt-14"} overflow-hidden bg-black ${height}`}>
      {video ? (
        <video ref={ref} src={video} poster={poster || (video && video.replace(/\.mp4$/, "-poster.jpg"))} preload="auto" autoPlay muted loop playsInline onLoadedMetadata={(e) => { e.currentTarget.playbackRate = rate; }}
          className={`bw-hover absolute inset-y-0 right-0 h-full ${sideVideo ? "w-full object-cover lg:w-1/2 lg:object-contain" : ratio ? (crop ? `w-full object-cover ${cropPos}` : "w-full object-cover") : "w-full object-cover"}`} />
      ) : (
        <div className="absolute inset-0 flex">
          {images.map((im, i) => (
            <div key={im.src} className={`relative flex-1 overflow-hidden ${i === 0 ? "" : "hidden sm:block"}`}>
              <img src={im.src} alt={im.alt || ""} className={`${rgb ? "rgb-hover" : "bw-hover"} absolute inset-0 w-full h-full object-cover`} style={{ objectPosition: im.pos || "50% 50%" }} />
            </div>
          ))}
        </div>
      )}
      {!noCaption && <BannerCaption label={label} title={title} subtitle={subtitle} as={as} />}
    </section>
  );
};
